import React, { useContext, useState } from "react";
import "./Zakelijk.css";
import { Helmet } from "react-helmet";
import {
  faPuzzlePiece,
  faCamera,
  faPaintBrush,
  faGamepad,
  faChevronRight,
  faChevronLeft,
  faQuoteLeft,
} from "@fortawesome/free-solid-svg-icons";
import swapLogo from "./img/swapLogo.png";
import arcadisLogo from "./img/Arcadis.png";
import schipholLogo from "./img/Schiphol.png";
import minfinLogo from "./img/Minfinlogo.png";
import kaapseLogo from "./img/kaapse.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AppContext } from "../shared/context/AppContext";

const FEATURES = [
  {
    icon: faCamera,
    title: "360° foto's op uw locatie",
    titleEN: "360° photos of your location",
    text: "We fotograferen de plekken die u kiest: een kantoor, museum, werkplaats of zelfs een kroeg.",
    textEN:
      "We photograph the places you choose: an office, a museum, a workshop or even a pub.",
  },
  {
    icon: faGamepad,
    title: "Een interactieve wereld",
    titleEN: "An interactive world",
    text: "Van de foto's bouwen we een virtuele omgeving waarin spelers rondlopen, zoeken en puzzels oplossen.",
    textEN:
      "We turn the photos into a virtual environment where players walk around, search and solve puzzles.",
  },
  {
    icon: faPaintBrush,
    title: "In uw eigen huisstijl",
    titleEN: "In your own branding",
    text: "Kleuren, logo's en boodschap: de hele escape room ademt uw organisatie.",
    textEN:
      "Colours, logos and message: the whole escape room feels like your organisation.",
  },
  {
    icon: faPuzzlePiece,
    title: "Puzzels op maat",
    titleEN: "Tailor-made puzzles",
    text: "Samen bedenken we puzzels die passen bij uw doel. Inspiratie hebben we genoeg.",
    textEN:
      "Together we design puzzles that fit your goal. We have plenty of inspiration.",
  },
];

const QUOTES = [
  {
    name: "Gyazi Johnson",
    logo: schipholLogo,
    company: "Schiphol",
    quote:
      "Bijzonder om het jaar samen af te sluiten, virtueel puzzelend in de vertrekhal van ons eigen Schiphol.",
    quoteEN:
      "It was a special experience to celebrate the holidays together, virtually puzzling on Schiphol Plaza.",
  },
  {
    name: "Bart de Jongh",
    logo: minfinLogo,
    company: "Ministerie van Financiën",
    quote:
      "Digitaal door ons kantoor als escape room, echt helemaal top. Mooi gedaan, we hadden een top teamuitje!",
    quoteEN:
      "Walking through our own office as a digital escape room was brilliant. Nicely done, we had a great team outing!",
  },
  {
    name: "Ella Greer",
    logo: swapLogo,
    company: "Swapfiets",
    quote:
      "Dankzij Escape Mystery doen al onze nieuwe collega's door Europa de onboarding virtueel en interactief. Alles ziet er professioneel uit!",
    quoteEN:
      "Thanks to Escape Mystery, all our new colleagues across Europe do the onboarding virtually and interactively. Everything looks professional!",
  },
  {
    name: "Lieke Arts",
    logo: arcadisLogo,
    company: "Arcadis",
    quote:
      "Dankzij Escape Mystery hadden wij een ontzettend leuk teamuitje. Iedereen heeft fanatiek gepuzzeld om bovenin ons klassement te eindigen.",
    quoteEN:
      "Thanks to Escape Mystery we had a really fun team outing. Everyone puzzled fanatically to finish at the top of our leaderboard.",
  },
  {
    name: "Duider Prins",
    logo: kaapseLogo,
    company: "Kaapse Maria",
    quote:
      "Gaaf om al puzzelend ook digitaal door onze kroeg heen te kunnen lopen. Foto's maken ging snel en het eindresultaat is super!",
    quoteEN:
      "Great to walk through our own pub digitally while puzzling. The photo shoot was quick and the end result is great!",
  },
];

const STEPS = [
  {
    title: "Kennismaking",
    titleEN: "Getting to know you",
    text: "We bespreken uw doel, de spelers en de opbouw van de escape room.",
    textEN: "We discuss your goal, the players and the outline of the escape room.",
  },
  {
    title: "Fotoshoot op locatie",
    titleEN: "Photo shoot on location",
    text: "We komen langs en maken 360° foto's van de gekozen plekken.",
    textEN: "We come by and take 360° photos of the chosen spots.",
  },
  {
    title: "Bouwen",
    titleEN: "Building",
    text: "We bouwen de escape room en de puzzels, helemaal naar uw wensen.",
    textEN: "We build the escape room and the puzzles, entirely to your wishes.",
  },
  {
    title: "Testen",
    titleEN: "Testing",
    text: "Ons testpanel speelt de escape room, en u natuurlijk ook.",
    textEN: "Our test panel plays the escape room, and so do you.",
  },
  {
    title: "Finetunen",
    titleEN: "Fine-tuning",
    text: "Alle feedback uit de tests verwerken we tot een soepel lopend spel.",
    textEN: "We turn all feedback from testing into a smoothly running game.",
  },
  {
    title: "Spelen!",
    titleEN: "Play!",
    text: "Uw escape room is klaar. Laat de teams maar komen!",
    textEN: "Your escape room is ready. Let the teams come!",
  },
];

function Zakelijk() {
  const { language } = useContext(AppContext);
  const nl = language === "NL";
  // Everything @escapemystery.nl reaches us, so each language gets its own word
  const businessEmail = nl ? "zakelijk@escapemystery.nl" : "business@escapemystery.nl";
  const [name, setName] = useState("");
  const [organization, setOrganization] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [quoteIndex, setQuoteIndex] = useState(0);

  const scrollQuote = (direction) => {
    setQuoteIndex(
      (index) => (index + QUOTES.length + direction) % QUOTES.length
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(false);
    const submitForm = async () => {
      try {
        const response = await fetch(
          process.env.REACT_APP_BACKENDURL + "/website/form/submit",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              name: name,
              organization: organization,
              email: email,
              message: message,
            }),
          }
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.errorMsg);
        }
        setSubmitted(true);
      } catch (err) {
        setError(true);
        console.log(err.message);
      }
    };
    submitForm();
  };

  const quote = QUOTES[quoteIndex];

  return (
    <main className="page" id="zakelijk">
      <Helmet>
        <title>
          {nl
            ? "Escape room op maat | Escape Mystery"
            : "Custom escape room | Escape Mystery"}
        </title>
      </Helmet>

      <section className="light-bg">
        <div className="page-intro">
          <h1>
            {nl
              ? "Uw eigen locatie als online escape room"
              : "Your own location as an online escape room"}
          </h1>
          <p>
            {nl
              ? "Wij maken van uw kantoor, museum of bedrijfspand een interactieve 360° escape room. Voor een teamuitje, onboarding, les of een spectaculaire onthulling."
              : "We turn your office, museum or workplace into an interactive 360° escape room. For a team outing, onboarding, a lesson or a spectacular reveal."}
          </p>
          <a href="#contact" className="blue-button auto-size intro-cta">
            {nl ? "Vraag vrijblijvend naar de mogelijkheden" : "Ask us what's possible"}
          </a>
        </div>
        <div className="page-content">
          <h2 className="centeralign">
            {nl ? "Wat wij voor u doen" : "What we do for you"}
          </h2>
          <ul className="feature-grid">
            {FEATURES.map((feature) => (
              <li className="feature-card" key={feature.text}>
                <span className="feature-icon" aria-hidden="true">
                  <FontAwesomeIcon icon={feature.icon} />
                </span>
                <div>
                  <h3>{nl ? feature.title : feature.titleEN}</h3>
                  <p>{nl ? feature.text : feature.textEN}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="grey-bg">
        <div className="page-content">
          <h2 className="centeralign">
            {nl ? "Wat klanten zeggen" : "What clients say"}
          </h2>
          <div className="quote-carousel">
            <button
              type="button"
              className="quote-arrow"
              aria-label={nl ? "Vorige" : "Previous"}
              onClick={() => scrollQuote(-1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <figure className="quote-card" aria-live="polite">
              <FontAwesomeIcon className="quote-mark" icon={faQuoteLeft} />
              <blockquote>{nl ? quote.quote : quote.quoteEN}</blockquote>
              <figcaption>
                <span className="quote-name">{quote.name}</span>
                <img src={quote.logo} alt={quote.company} />
              </figcaption>
            </figure>
            <button
              type="button"
              className="quote-arrow"
              aria-label={nl ? "Volgende" : "Next"}
              onClick={() => scrollQuote(1)}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
          <div className="quote-dots">
            {QUOTES.map((q, index) => (
              <button
                type="button"
                key={q.name}
                className={index === quoteIndex ? "active" : undefined}
                aria-label={q.company}
                aria-current={index === quoteIndex}
                onClick={() => setQuoteIndex(index)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="light-bg">
        <div className="page-content">
          <h2 className="centeralign">
            {nl
              ? "In 6 stappen naar uw eigen escape room"
              : "Your own escape room in 6 steps"}
          </h2>
          <ol className="step-grid">
            {STEPS.map((step, index) => (
              <li className="step-card" key={step.title}>
                <span className="step-number" aria-hidden="true">
                  {index + 1}
                </span>
                <div>
                  <h3>{nl ? step.title : step.titleEN}</h3>
                  <p>{nl ? step.text : step.textEN}</p>
                </div>
              </li>
            ))}
          </ol>
          {/* A one-room demo in the escape room app (01_escaperooms, demoER) */}
          <a
            href={`https://escapemystery.nl/play/demo${nl ? "" : "?lang=en"}`}
            target="_blank"
            rel="noopener noreferrer"
            className="blue-button auto-size demo-button"
          >
            {nl ? "Probeer de gratis demo" : "Try the free demo"}
          </a>
        </div>
      </section>

      <section className="grey-bg" id="contact">
        <div className="page-content narrow">
          <h2 className="centeralign no-margin-bottom">
            {nl ? "Vertel ons over uw idee" : "Tell us about your idea"}
          </h2>
          <p className="centeralign contact-intro">
            {nl
              ? "Vrijblijvend, en we denken graag met u mee."
              : "No strings attached, and we're happy to think along."}
          </p>

          <form className="contact-form" onSubmit={handleSubmit}>
            <label>
              {nl ? "Naam" : "Name"}*
              <input
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </label>
            <label>
              {nl ? "Organisatie" : "Organization"}
              <input
                type="text"
                autoComplete="organization"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
              />
            </label>
            <label>
              {nl ? "E-mailadres" : "E-mail address"}*
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              {nl ? "Bericht" : "Message"}*
              <textarea
                placeholder={
                  nl
                    ? "Bijvoorbeeld: een teamuitje voor 40 collega's in ons kantoor, rond december."
                    : "For example: a team outing for 40 colleagues in our office, around December."
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              />
            </label>
            {submitted ? (
              <div className="form-status success" role="status">
                {nl
                  ? "Bedankt! Uw bericht is verstuurd, we nemen snel contact met u op."
                  : "Thank you! Your message has been sent, we'll be in touch soon."}
              </div>
            ) : (
              <button type="submit" className="blue-button" id="submit-email">
                {nl ? "Verstuur bericht" : "Send message"}
              </button>
            )}
          </form>

          {error && (
            <div className="form-status error" role="alert">
              {nl
                ? "Er ging iets mis bij het verzenden. Probeer het nog eens."
                : "Something went wrong while sending. Please try again."}
            </div>
          )}

          <p className="centeralign contact-alt">
            {nl ? "Of stuur een e-mail naar " : "Or send us an e-mail at "}
            <a href={`mailto:${businessEmail}`}>{businessEmail}</a>
          </p>
        </div>
      </section>
    </main>
  );
}

export default Zakelijk;
